/**
 * @typedef {{ title: string, accessTargetId: string, readonly?: boolean, isTab?: boolean }} ComponentAccessTarget
 * @typedef {{
 *    title: string,
 *    pageId: string,
 *    type: 'page' | 'pageList' |'tab' | 'menu',
 *    tabs: ComponentAccessTarget[],
 *    actions: ComponentAccessTarget[],
 * }} PageAccessTarget
 *
 * @typedef {{
 *   [role:string]: {
 *        [accessTargetId: string]: boolean
 *   }
 * }} ComponentAccess
 * 
 * @typedef {{ 
 *   [pageId: string] : {
 *        mobile: boolean,
 *        desktop: boolean,
 *        [accessTargetId: string]: boolean
 *   }
 * }} PagesAccess
 * 
 * @typedef {{
 *   [role:string]: PagesAccess
 * }} RoleAccess
 *

 * @callback ExtractAccessFunction
 * Extract both the "handles" for where permissions can be applied to (the `spec`),
 * as well as the actual permissions (`access`) for each role as requested.
 * Most of the time, only either the `spec`, or the `access` is needed,
 * But as they are so strongly tied together,
 * extracting them together helps in using the exact same approach for both.
 *
 * The format of `spec` and `access` depend on the level of the tree we are processing.
 * If we are deeper in the tree, and we are working only individual components, it's Component*
 * If we are on navHeading or higher levels, it's Page*.
 * @param {object} hNode
 * @param {object} options
 * @param {string[]} options.roles
 * @param {boolean} [options.isTab=false]
 * @returns {{
 *   spec: ComponentAccessTarget[] | PageAccessTarget[],
 *   access: ComponentAccess | RoleAccess
 * }} see also lib_metadata-transforms\src\blockDefinition\blockGroups\formElements\types\PageAccess.js
 *
 * @callback RegisterFunction
 * @param {string} hNodeTypeGroup
 * @param {{ extractAccess:extractAccessFunction }} implementation
 * @returns {void}
 */

/**
 * @type {{
 * register: RegisterFunction
 * addAccess: (access:ComponentAccess, hNode:object, options:{roles:string[], isTab:boolean}) => ComponentAccess
 * addSpec: (spec:ComponentAccessTarget[], hNode:object, options:{isTab:boolean}) => ComponentAccessTarget[]
 * hNode:{ extractAccess:ExtractAccessFunction }
 * [hNodeTypeGroup:string]:{
 *     [hNodeType:string]:{ extractAccess:ExtractAccessFunction }
 * }
 * }}
 */
let blockGroups = {};

blockGroups.register = function register(hNodeTypeGroup, implementation) {
    if (!blockGroups[hNodeTypeGroup]) {
        blockGroups[hNodeTypeGroup] = {};
    }
    blockGroups[hNodeTypeGroup] = implementation;
};

function getReferenceId(hNode, isTab) {
    const { id, customizableVisibility, title } = hNode;
    return customizableVisibility?.[0]?.accessTargetId || (isTab ? `tab_${title}` : id);
}

// Calculate the access for the spec
blockGroups.addAccess = function addAccess(access, hNode, { roles, isTab = false }) {
    if (hNode.hiddenFor?.includes('ADMIN') && hNode.hiddenFor?.includes('USER')) {
        return access;
    }
    const accessTargetId = getReferenceId(hNode, isTab);

    return roles.reduce((acc, role) => {
        acc[role] = acc[role] || {};
        acc[role][accessTargetId] = !hNode.hiddenFor?.includes(role);
        return acc;
    }, access);
};

// Define the spec for the access
blockGroups.addSpec = function addSpec(spec, hNode, { isTab }) {
    if (hNode.hiddenFor?.includes('ADMIN') && hNode.hiddenFor?.includes('USER')) {
        return spec;
    }
    if (hNode.customizableVisibility) {
        return spec.concat(hNode.customizableVisibility);
    }
    const accessTargetId = getReferenceId(hNode, isTab);

    return spec.concat({ title: hNode.title, accessTargetId, readonly: false, isTab });
};

module.exports = blockGroups;
